<template>
  <div class="float-chat-window">
    <!-- 初始化加载 -->
    <div v-if="isInitializing && !showVariableConfigPanel" class="initializing-loader">
      <div class="loader-content">
        <div class="loader-icon">
          <img :src="botAvatar" alt="AI助手" />
        </div>
        <div class="loader-text">正在初始化...</div>
        <div class="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>

    <!-- 变量配置选择界面 -->
    <div v-else-if="showVariableConfigPanel" class="variable-config-panel">
      <div class="config-panel-content">
        <!-- 图标和标题区域 -->
        <div class="config-panel-header">
          <div class="header-icon">
            <img :src="botAvatar" alt="AI助手" />
          </div>
          <div class="header-title">{{ variableConfigDialogTitle }}</div>
        </div>

        <!-- 配置项列表 -->
        <div class="config-items">
          <div v-for="(config, index) in variableConfigs" :key="index" class="config-item">
            <!-- 标签组 -->
            <div class="label-group">
              <div class="label-tag" v-if="config.category">{{ config.category }}</div>
              <div class="label-main">
                <span>{{ config.label || config.name }}</span>
              </div>
            </div>
            <!-- 输入控件 -->
            <div class="input-wrapper">
              <Select v-if="config.variableType === 'Enum' || config.options"
                v-model="variableConfigValues[config.key || config.name]" :placeholder="config.placeholder || '请选择'"
                filterable clearable>
                <Option v-for="option in config.enumValues" :key="option" :value="option">
                  {{ option }}
                </Option>
              </Select>
              <Input v-else v-model="variableConfigValues[config.key || config.name]"
                :placeholder="config.placeholder || '请输入'" :type="config.type || 'text'" />
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="config-panel-footer">
          <button class="button" type="primary" shape="circle" :disabled="!canSubmitVariableConfig"
            @click="handleVariableConfigSubmit">
            开始会话
          </button>
        </div>
      </div>
    </div>

    <!-- 聊天主体 -->
    <div v-else class="chat-main">
      <!-- 聊天内容区 -->
      <div class="chat-body" ref="chatBody">
        <!-- 欢迎区域 -->
        <div class="welcome-section" v-if="messages.length === 0">
          <div class="welcome-avatar">
            <img :src="botAvatar" alt="AI助手" />
          </div>
          <h3 class="welcome-title">{{ welcomeTitle }}</h3>
          <p class="welcome-desc">{{ welcomeDescription }}</p>
          
          <!-- 快速问题（来自配置） -->
          <div class="quick-questions" v-if="welcomeQuickQuestions && welcomeQuickQuestions.length > 0">
            <div class="section-title">快速提问</div>
            <button
              v-for="(question, index) in welcomeQuickQuestions"
              :key="index"
              class="quick-btn"
              @click="sendQuickQuestion(question)"
            >
              {{ question }}
            </button>
          </div>
        </div>

        <!-- 消息列表 -->
        <div class="messages-list">
          <div
            v-for="(message, index) in messages"
            :key="message.id || index"
            class="message-item"
            :class="{ 'user-msg': message.role === 'user', 'bot-msg': message.role === 'bot' }"
          >
            <!-- AI助手消息 -->
            <template v-if="message.role === 'bot'">
              <!-- 头像 -->
              <div class="msg-avatar">
                <img :src="botAvatar" alt="AI助手" />
              </div>
              
              <!-- 内容 -->
              <div class="msg-content">
                <div class="msg-header">
                  <span class="msg-name">{{ botName }}</span>
                  <span class="msg-time">{{ formatTime(message.timestamp) }}</span>
                </div>
                <div class="msg-body">
                  <!-- 文本消息 -->
                  <div v-if="message.type === 'text'" class="text-msg" v-html="renderMessage(message.content)"></div>
                  
                  <!-- 加载中 -->
                  <div v-if="message.type === 'loading'" class="loading-msg">
                    <div class="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>

                  <!-- 错误消息 -->
                  <div v-if="message.type === 'error'" class="error-msg">
                    <span>{{ message.content }}</span>
                    <button v-if="message.retryable" class="retry-btn" @click="retryMessage(message)">重试</button>
                  </div>
                </div>
              </div>
            </template>

            <!-- 用户消息 -->
            <template v-else>
              <!-- 内容 -->
              <div class="msg-content">
                <div class="msg-header">
                  <span class="msg-time">{{ formatTime(message.timestamp) }}</span>
                  <span class="msg-name">{{ currentUserName }}</span>
                </div>
                <div class="msg-body">
                  <!-- 文本消息 -->
                  <div v-if="message.type === 'text'" class="text-msg" v-html="renderMessage(message.content)"></div>
                </div>
              </div>
              
              <!-- 头像 -->
              <div class="msg-avatar">
                <img :src="currentUserAvatar" alt="用户" />
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- 输入区域 -->
      <div class="chat-footer">
        <div class="input-wrapper">
          <textarea
            ref="inputTextarea"
            v-model="inputText"
            placeholder="输入消息..."
            @keydown.enter.exact.prevent="sendMessage"
            @input="adjustTextareaHeight"
            rows="1"
            class="input-area"
          ></textarea>
          <button class="send-btn" :disabled="!canSend" @click="sendMessage">
            发送
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { session, cookie } from '@/libs/location'
import { Select, Option, Input, Message } from 'view-design'
import robot from '@/assets/images/common/robot.png'
import defaultAvatar from '@/assets/images/avator.png'

// 全局缓存对象，用于在组件销毁后保持数据
const globalCache = {
  hasInitialized: false,
  quickFuncList: [],
  problemList: [],
  appConfig: null,
  conversationID: null,
  appConversationID: null,
  messages: [],
  currentAgentId: null
}

export default {
  name: 'FloatChatWindow',
  components: {
    Select,
    Option,
    Input
  },
  props: {
    agentId: {
      type: [String, Number],
      default: null
    },
    // 是否保持缓存（默认 true，浮动窗口不刷新时不重新初始化）
    keepAlive: {
      type: Boolean,
      default: true
    },
    // 从父组件传入的快速功能列表（用于头部下拉）
    quickFuncList: {
      type: Array,
      default: () => []
    },
    // 从父组件传入的常用问题列表（用于头部下拉）
    problemList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // 基础配置
      botName: 'AI助手',
      botAvatar: robot,
      
      // 消息相关（从缓存恢复）
      messages: [...globalCache.messages],
      inputText: '',
      
      // 状态
      isInitializing: !globalCache.hasInitialized,
      isLoading: false,
      
      // 会话信息（从缓存恢复）
      conversationID: globalCache.conversationID,
      appConversationID: globalCache.appConversationID,
      appConfig: globalCache.appConfig,
      userInfo: null,
      
      // 变量配置相关
      showVariableConfigPanel: false,
      variableConfigs: [],
      variableConfigValues: {},
      variableConfigSelected: false,
      
      // 选中的快速功能
      selectedQuickFunc: null
    }
  },
  computed: {
    canSend() {
      return this.inputText.trim().length > 0 && !this.isLoading
    },
    welcomeTitle() {
      if (this.appConfig) {
        return this.appConfig.welcomeTitle || this.appConfig.botName || this.botName
      }
      return this.botName
    },
    welcomeDescription() {
      if (this.appConfig) {
        return this.appConfig.welcomeDescription || this.appConfig.description || '有什么可以帮助您的吗？'
      }
      return '有什么可以帮助您的吗？'
    },
    welcomeQuickQuestions() {
      if (this.appConfig && this.appConfig.openQuery) {
        return this.appConfig.openQuery
      }
      return []
    },
    currentUserName() {
      if (this.userInfo) {
        return this.userInfo.userName || this.userInfo.realName || '用户'
      }
      return '用户'
    },
    currentUserAvatar() {
      if (this.userInfo) {
        return this.userInfo.headImg || defaultAvatar
      }
      return defaultAvatar
    },
    // 是否可以提交变量配置
    canSubmitVariableConfig() {
      if (!this.variableConfigs || this.variableConfigs.length === 0) {
        return false
      }
      // 检查所有必填项是否都已填写
      return this.variableConfigs.every(config => {
        const key = config.key || config.name
        const value = this.variableConfigValues[key]
        if (config.required !== false) {
          return value !== undefined && value !== null && value !== ''
        }
        return true
      })
    },
    // 变量配置对话框标题
    variableConfigDialogTitle() {
      if (this.appConfig) {
        const appName = this.appConfig.botName || this.appConfig.name || ''
        if (appName) {
          return `${appName}·对话设置`
        }
      }
      return '对话设置'
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
        // 同步到全局缓存
        globalCache.messages = [...this.messages]
      },
      deep: true
    },
    conversationID(val) {
      globalCache.conversationID = val
    },
    appConversationID(val) {
      globalCache.appConversationID = val
    },
    agentId: {
      handler (newVal, oldVal) {
        if (newVal === oldVal) return
        if (!newVal) return
        if (!globalCache.hasInitialized) {
          this.tryFirstInitIfNeeded()
          return
        }
        if (oldVal !== undefined) {
          this.switchAgent(newVal)
        }
      },
      immediate: false
    }
  },
  async mounted () {
    if (globalCache.hasInitialized) {
      console.log('使用缓存数据，跳过接口调用')
      this.isInitializing = false
      return
    }
    await this.tryFirstInitIfNeeded()
  },
  methods: {
    /** 无 agentId 时不调 getAppConfig / createConversation，避免接口报错；列表异步返回后由 watch agentId 触发 */
    async tryFirstInitIfNeeded () {
      if (globalCache.hasInitialized || !this.agentId) {
        this.isInitializing = false
        return
      }
      console.log('首次初始化（已有 agentId），调用接口')
      try {
        this.isInitializing = true
        this.loadUserInfo()
        await this.getAppConfig(this.agentId)
        await this.createConversation()
        globalCache.hasInitialized = true
        globalCache.currentAgentId = this.agentId
      } finally {
        this.isInitializing = false
      }
    },

    // 加载用户信息
    loadUserInfo() {
      try {
        const userInfoStr = session.getItem('userInfo')
        if (userInfoStr) {
          this.userInfo = JSON.parse(userInfoStr)
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
      }
    },

    // 获取默认图标
    getDefaultIcon(index) {
      const icons = [
        require('@/assets/images/common/richeng.png'),
        require('@/assets/images/common/renwu.png'),
        require('@/assets/images/common/zhishi.png'),
        require('@/assets/images/common/shuju.png')
      ]
      return icons[index % icons.length]
    },

    // 获取应用配置
    async getAppConfig(agentId = this.agentId) {
      try {
        const requestData = {}
        if (agentId) {
          requestData.agentId = agentId
        }
        const response = await this.$request.post('/sso/hiAgent/getAppConfigPreview', requestData)
        const { code, data } = response.data
        if (code === 'OK' && data) {
          this.appConfig = data
          this.botName = data.botName || data.name || this.botName
          
          // 处理变量配置
          if (data.variableConfigs && Array.isArray(data.variableConfigs) && data.variableConfigs.length > 0) {
            this.variableConfigs = data.variableConfigs
            this.variableConfigSelected = false
            this.initVariableConfigValues()
            this.showVariableConfigPanel = true
            this.isInitializing = false
          } else {
            this.showVariableConfigPanel = false
            this.variableConfigSelected = true
            // 同步到全局缓存
            globalCache.appConfig = data
          }
        }
      } catch (error) {
        console.error('获取应用配置失败:', error)
      }
    },

    // 创建会话
    async createConversation() {
      try {
        const requestData = {}
        if (this.agentId) {
          requestData.agentId = this.agentId
        }
        // 如果有变量配置值，将其添加到请求数据中
        if (this.variableConfigValues && Object.keys(this.variableConfigValues).length > 0) {
          requestData.inputs = this.variableConfigValues
        }
        const response = await this.$request.post('/sso/hiAgent/createConversation', requestData)
        const { code, data } = response.data
        if (code === 'OK' && data) {
          if (data.conversation && data.conversation.conversationID) {
            this.conversationID = data.conversation.conversationID
          }
          if (data.conversation && data.conversation.appConversationID) {
            this.appConversationID = data.conversation.appConversationID
          }
        }
      } catch (error) {
        console.error('创建会话失败:', error)
      }
    },

    // 发送消息
    async sendMessage() {
      if (!this.canSend) return

      const text = this.inputText.trim()
      if (!text) return

      // 添加用户消息
      const userMessage = this.addMessage({
        role: 'user',
        type: 'text',
        content: text,
        timestamp: new Date()
      })

      // 清空输入
      this.inputText = ''
      this.adjustTextareaHeight()

      // 发送到API
      await this.sendMessageToAPI(text, userMessage)
    },

    // 初始化变量配置默认值
    initVariableConfigValues() {
      this.variableConfigValues = {}
      if (this.variableConfigs && this.variableConfigs.length > 0) {
        this.variableConfigs.forEach(config => {
          const key = config.key || config.name
          this.variableConfigValues[key] = config.defaultValue || undefined
        })
      }
    },
    
    // 处理变量配置提交
    async handleVariableConfigSubmit() {
      if (!this.canSubmitVariableConfig) {
        Message.warning('请填写所有必填项')
        return
      }
      try {
        // 显示初始化加载动画
        this.isInitializing = true
        // 标记已选择配置
        this.variableConfigSelected = true
        // 隐藏配置面板
        this.showVariableConfigPanel = false
        // 初始化会话
        await this.createConversation()
      } finally {
        // 隐藏初始化加载动画
        this.isInitializing = false
      }
    },
    
    // 切换agent
    async switchAgent(newAgentId) {
      if (newAgentId === (this.agentId || globalCache.currentAgentId)) return
      
      console.log('切换agent到:', newAgentId)
      
      // 重置对话状态
      this.messages = []
      this.conversationID = null
      this.appConversationID = null
      
      // 更新全局缓存中的agentId
      globalCache.currentAgentId = newAgentId
      
      // 重新初始化
      this.isInitializing = true
      try {
        await this.getAppConfig(newAgentId)
        await this.createConversation()
        globalCache.hasInitialized = true

        // 重置输入框
        this.inputText = ''
        this.adjustTextareaHeight()
      } finally {
        this.isInitializing = false
      }
    },
    
    // 发送快速问题
    sendQuickQuestion(question) {
      this.inputText = question
      this.sendMessage()
    },

    // 添加消息
    addMessage(message) {
      const newMessage = {
        id: Date.now() + Math.random(),
        role: message.role || 'user',
        type: message.type || 'text',
        content: message.content || '',
        timestamp: message.timestamp || new Date(),
        retryable: message.retryable || false
      }
      this.messages.push(newMessage)
      return newMessage
    },

    // 调用API发送消息（流式）
    async sendMessageToAPI(text, userMessage) {
      this.isLoading = true

      // 添加加载消息
      const loadingMessage = this.addMessage({
        role: 'bot',
        type: 'loading',
        content: '',
        timestamp: new Date()
      })

      try {
        const requestData = {
          query: text,
          conversationID: this.conversationID,
          appConversationID: this.appConversationID
        }
        if (this.agentId) {
          requestData.agentId = this.agentId
        }

        const token = session.parseJSON() || cookie.getCookie('token') || ''
        const baseURL = this.$baseUrl || window.location.origin + '/'
        const url = baseURL.replace(/\/$/, '') + '/sso/hiAgent/chatQueryV2ByStream'

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'authorization': token || ''
          },
          body: JSON.stringify(requestData)
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        await this.handleStreamResponse(response, loadingMessage)
      } catch (error) {
        console.error('发送消息失败:', error)
        this.handleAPIError(error, loadingMessage, text)
      } finally {
        this.isLoading = false
      }
    },

    // 处理流式响应
    async handleStreamResponse(response, loadingMessage) {
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''
      let isFirstChunk = true

      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.trim() === '') continue
            if (line.startsWith('data:')) {
              try {
                const dataStr = line.substring(5)
                if (dataStr.trim() === '[DONE]') break

                const data = JSON.parse(dataStr)
                if (data.content || data.text || data.answer || data.delta) {
                  const chunk = data.content || data.text || data.answer || data.delta || ''
                  if (chunk) {
                    fullContent += chunk
                    if (isFirstChunk) {
                      loadingMessage.type = 'text'
                      loadingMessage.content = fullContent
                      isFirstChunk = false
                    } else {
                      loadingMessage.content = fullContent
                    }
                  }
                }

                if (data.done || data.finished || data.stop) {
                  if (fullContent) {
                    loadingMessage.content = fullContent
                  }
                  if (data.conversationID) {
                    this.conversationID = data.conversationID
                  }
                  if (data.appConversationID) {
                    this.appConversationID = data.appConversationID
                  }
                  break
                }
              } catch (e) {
                console.warn('解析流式数据失败:', e)
              }
            }
          }
        }

        if (fullContent && loadingMessage.type === 'loading') {
          loadingMessage.type = 'text'
          loadingMessage.content = fullContent
        }
      } catch (error) {
        console.error('读取流式响应失败:', error)
        throw error
      } finally {
        reader.releaseLock()
      }
    },

    // 处理API错误
    handleAPIError(error, loadingMessage, text) {
      const loadingIndex = this.messages.findIndex(m => m.id === loadingMessage.id)
      if (loadingIndex > -1) {
        this.messages.splice(loadingIndex, 1)
      }

      const errorMessage = this.addMessage({
        role: 'bot',
        type: 'error',
        content: error.message || '网络错误，请稍后重试',
        timestamp: new Date(),
        retryable: true
      })

      errorMessage.retryData = { text }
    },

    // 重试消息
    async retryMessage(errorMessage) {
      if (!errorMessage.retryData) return

      const errorIndex = this.messages.findIndex(m => m.id === errorMessage.id)
      if (errorIndex > -1) {
        this.messages.splice(errorIndex, 1)
      }

      await this.sendMessageToAPI(errorMessage.retryData.text, null)
    },

    // 渲染消息（支持简单Markdown）
    renderMessage(content) {
      return content
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>')
        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>')
    },

    // 格式化时间
    formatTime(timestamp) {
      const date = new Date(timestamp)
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${hours}:${minutes}`
    },

    // 滚动到底部
    scrollToBottom() {
      const chatBody = this.$refs.chatBody
      if (chatBody) {
        chatBody.scrollTop = chatBody.scrollHeight
      }
    },

    // 调整输入框高度
    adjustTextareaHeight() {
      this.$nextTick(() => {
        const textarea = this.$refs.inputTextarea
        if (textarea) {
          textarea.style.height = 'auto'
          textarea.style.height = Math.min(textarea.scrollHeight, 80) + 'px'
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
.float-chat-window {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;

  // 变量配置面板
  .variable-config-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    
    .config-panel-content {
      width: 100%;
      max-width: 400px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      
      .config-panel-header {
        display: flex;
        align-items: center;
        padding: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        
        .header-icon {
          width: 40px;
          height: 40px;
          margin-right: 12px;
          
          img {
            width: 100%;
            height: 100%;
            border-radius: 50%;
          }
        }
        
        .header-title {
          font-size: 18px;
          font-weight: 600;
        }
      }
      
      .config-items {
        padding: 20px;
        
        .config-item {
          margin-bottom: 20px;
          
          .label-group {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
            
            .label-tag {
              background: #e0e0e0;
              color: #666;
              padding: 2px 8px;
              border-radius: 4px;
              font-size: 12px;
              margin-right: 8px;
            }
            
            .label-main {
              font-size: 14px;
              font-weight: 500;
              color: #333;
            }
          }
          
          .input-wrapper {
            /deep/ .ivu-select, /deep/ .ivu-input {
              width: 100%;
            }
          }
        }
      }
      
      .config-panel-footer {
        padding: 0 20px 20px;
        text-align: center;
        
        .button {
          width: 100%;
          height: 40px;
          background: #2d8cf0;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s;
          
          &:hover:not(:disabled) {
            background: #2b85e4;
          }
          
          &:disabled {
            background: #ccc;
            cursor: not-allowed;
          }
        }
      }
    }
  }

  // 初始化加载
  .initializing-loader {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .loader-content {
      text-align: center;
      
      .loader-icon {
        width: 50px;
        height: 50px;
        margin: 0 auto 15px;
        
        img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
        }
      }
      
      .loader-text {
        font-size: 14px;
        color: #666;
        margin-bottom: 10px;
      }
    }
  }

  // 聊天主体
  .chat-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  // 聊天内容区
  .chat-body {
    flex: 1;
    overflow-y: auto;
    padding: 15px;
    background: #f5f7f9;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 2px;
    }

    // 欢迎区域
    .welcome-section {
      text-align: center;
      padding: 20px 15px;
      
      .welcome-avatar {
        width: 50px;
        height: 50px;
        margin: 0 auto 15px;
        
        img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
        }
      }
      
      .welcome-title {
        font-size: 16px;
        color: #333;
        margin: 0 0 8px;
      }
      
      .welcome-desc {
        font-size: 13px;
        color: #666;
        margin: 0 0 20px;
      }
      
      .section-title {
        font-size: 14px;
        font-weight: 500;
        color: #333;
        margin: 15px 0 10px;
        text-align: left;
      }
      
      // 快速功能列表
      .quick-functions {
        .func-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          
          .func-item {
            display: flex;
            align-items: center;
            padding: 10px;
            background: #fff;
            border: 1px solid #e8eaec;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.3s;
            text-align: left;
            
            img {
              width: 36px;
              height: 36px;
              margin-right: 10px;
              border-radius: 6px;
              flex-shrink: 0;
            }
            
            .func-info {
              flex: 1;
              min-width: 0;
              
              .func-name {
                font-size: 13px;
                color: #333;
                font-weight: 500;
                margin-bottom: 2px;
              }
              
              .func-desc {
                font-size: 12px;
                color: #999;
                display: block;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
            
            &:hover {
              border-color: #2d8cf0;
              background: #f0f7ff;
            }
            
            &.active {
              border-color: #2d8cf0;
              background: #e8f4ff;
            }
          }
        }
      }
      
      // 常用问题列表
      .common-problems {
        .problem-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
          
          .problem-btn {
            padding: 8px 12px;
            background: #fff;
            border: 1px solid #e8eaec;
            border-radius: 6px;
            font-size: 13px;
            color: #515a6e;
            cursor: pointer;
            transition: all 0.3s;
            text-align: left;
            
            &:hover {
              background: #2d8cf0;
              color: #fff;
              border-color: #2d8cf0;
            }
          }
        }
      }
      
      // 快速提问（来自配置）
      .quick-questions {
        display: flex;
        flex-direction: column;
        gap: 8px;
        
        .quick-btn {
          padding: 10px 15px;
          background: #fff;
          border: 1px solid #e8eaec;
          border-radius: 6px;
          font-size: 13px;
          color: #515a6e;
          cursor: pointer;
          transition: all 0.3s;
          
          &:hover {
            background: #2d8cf0;
            color: #fff;
            border-color: #2d8cf0;
          }
        }
      }
    }

    // 消息列表
    .messages-list {
      .message-item {
        display: flex;
        gap: 10px;
        margin-bottom: 15px;
                
        &.bot-msg {
          flex-direction: row;
        }
                
        &.user-msg {
          justify-content: flex-end;
          
          .msg-content {
            align-items: flex-end;
            text-align: right;
          }
          
          .msg-header {
            justify-content: flex-end;
            
            .msg-time {
              margin-left: 8px;
            }
          }
          
          .msg-body .text-msg {
            background: #2d8cf0;
            color: #fff;
          }
        }
        
        .msg-avatar {
          width: 32px;
          height: 32px;
          flex-shrink: 0;
          
          img {
            width: 100%;
            height: 100%;
            border-radius: 50%;
          }
        }
        
        .msg-content {
          flex: 1;
          min-width: 0;
          
          .msg-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 4px;
            
            .msg-name {
              font-size: 12px;
              color: #999;
            }
            
            .msg-time {
              font-size: 11px;
              color: #ccc;
            }
          }
          
          .msg-body {
            .text-msg {
              padding: 8px 12px;
              background: #fff;
              border-radius: 8px;
              font-size: 14px;
              line-height: 1.6;
              color: #333;
              word-wrap: break-word;
              
              /deep/ code {
                padding: 2px 4px;
                background: #f5f7f9;
                border-radius: 3px;
                font-size: 13px;
              }
              
              /deep/ a {
                color: #2d8cf0;
                text-decoration: none;
                
                &:hover {
                  text-decoration: underline;
                }
              }
            }
            
            .loading-msg {
              padding: 8px 12px;
            }
            
            .error-msg {
              padding: 8px 12px;
              background: #fff1f0;
              border: 1px solid #ffccc7;
              border-radius: 8px;
              font-size: 13px;
              color: #cf1322;
              display: flex;
              align-items: center;
              gap: 8px;
              
              .retry-btn {
                padding: 2px 8px;
                background: #cf1322;
                color: #fff;
                border: none;
                border-radius: 4px;
                font-size: 12px;
                cursor: pointer;
                
                &:hover {
                  background: #a8071a;
                }
              }
            }
          }
        }
        

      }
    }
  }

  // 输入区域
  .chat-footer {
    padding: 10px 15px;
    background: #fff;
    border-top: 1px solid #e8eaec;
    
    .input-wrapper {
      display: flex;
      gap: 8px;
      
      .input-area {
        flex: 1;
        min-height: 36px;
        max-height: 80px;
        padding: 8px 12px;
        border: 1px solid #dcdee2;
        border-radius: 4px;
        font-size: 13px;
        resize: none;
        outline: none;
        font-family: inherit;
        
        &:focus {
          border-color: #2d8cf0;
        }
      }
      
      .send-btn {
        padding: 0 15px;
        height: 36px;
        background: #2d8cf0;
        color: #fff;
        border: none;
        border-radius: 4px;
        font-size: 13px;
        cursor: pointer;
        transition: background 0.3s;
        
        &:hover:not(:disabled) {
          background: #2b85e4;
        }
        
        &:disabled {
          background: #ccc;
          cursor: not-allowed;
        }
      }
    }
  }

  // 打字指示器
  .typing-indicator {
    display: flex;
    align-items: center;
    gap: 4px;
    
    span {
      width: 6px;
      height: 6px;
      background: #ccc;
      border-radius: 50%;
      animation: typing 1.4s infinite;
      
      &:nth-child(2) {
        animation-delay: 0.2s;
      }
      
      &:nth-child(3) {
        animation-delay: 0.4s;
      }
    }
  }

  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
}
</style>
